import Fuse, { type IFuseOptions } from 'fuse.js';
import type { Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';

import {
  isReceiptAttachment,
  type TransactionProps,
  type TransactionRecord,
} from 'app-components/flux/transactions/transactionsTypes';

import { isLocalReceipt } from './receiptsPredicates';
import type { Receipt } from './receiptsTypes';

// Receipt totals and transaction amounts both carry cents; allow a penny of rounding slack.
// Compared in integer cents to avoid floating-point noise (e.g. 25.01 - 25 > 0.01).
const AMOUNT_TOLERANCE_CENTS = 1;
const toCents = (value: number): number => Math.round(value * 100);
// Window (in days) on either side of the receipt's transaction date. Receipts are dated by purchase
// day; the bank transaction posts a few days later.
const DATE_WINDOW_DAYS = 5;
const TIP_DATE_WINDOW_DAYS = 3;
const TIP_MERCHANT_THRESHOLD = 0.15;

export type ReceiptTransactionMatch = {
  transactionId: string;
  amountMatch: 'exact' | 'known-tip' | 'possible-tip';
  receiptAmount: number;
  transactionAmount: number;
};

/** Shown while receipt details or the picker are still looking for a suggestion. */
export const RECEIPT_SUGGESTION_LOADING_LABEL = 'Finding suggestions for this receipt';

/** Suggestion card header, shared so web and mobile, details and picker all read the same. */
export const getSuggestedMatchHeaderLabel = (match: ReceiptTransactionMatch | undefined): string =>
  match && match.amountMatch !== 'exact'
    ? 'Possible match — amount may include a tip'
    : 'Suggested transaction for this receipt';

type ReceiptMatchCandidate = {
  id: string;
  payee: string;
  postedOn: string;
  dayGap: number;
  amountCents: number;
  amountMatch: ReceiptTransactionMatch['amountMatch'];
};

// Use the same conservative threshold as the client's saved-report search. Bank descriptors can
// put the merchant anywhere and append store/location details, so ignore location and field length.
const MERCHANT_MATCH_OPTIONS: IFuseOptions<ReceiptMatchCandidate> = {
  keys: ['payee'],
  includeScore: true,
  threshold: 0.3,
  ignoreLocation: true,
  ignoreFieldNorm: true,
  ignoreDiacritics: true,
  shouldSort: false,
};

const normalizeMerchantName = (name: string): string =>
  name.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase().replace(/\s+/g, ' ');

const hasWholeWordNameMatch = (merchant: string, payee: string): boolean => {
  // Treat punctuation as word boundaries, including for merchant names outside the Latin alphabet.
  const merchantWords = merchant.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const payeeWords = payee.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  return (
    merchantWords.length > 0 &&
    payeeWords.length > 0 &&
    (` ${merchantWords} `.includes(` ${payeeWords} `) || ` ${payeeWords} `.includes(` ${merchantWords} `))
  );
};

export type ReceiptMatchCriteria = {
  /** The receipt total, compared by magnitude before trying guarded tip fallbacks. */
  total: number | undefined;
  /** Used to reconcile whether the receipt total includes an explicitly extracted tip. */
  subtotal?: number;
  tax?: number;
  tip?: number;
  /** Optional merchant name; requires whole-word containment in either direction or a close fuzzy match. */
  merchant?: string;
  /** Optional ISO purchase date; when present, the transaction must post within ±5 days. */
  transactionDate?: string;
  /** Optional account scope; when non-empty, only transactions in these accounts are considered. */
  accountIds?: string[];
  /** Allows this receipt's stale transaction attachment immediately after a successful detach. */
  receiptId?: string;
  /** Today (ISO); future-dated rows are never suggested. */
  today?: string;
};

/** A receipt can only anchor a search if it has a non-zero total. Merchant/date are optional. */
export const canSearchForMatch = (total: number | undefined): total is number =>
  total !== undefined && Number.isFinite(total) && total !== 0;

/**
 * A transaction is linkable while it has no receipt attachment and is not an unaccepted scheduled reminder
 * (the link pickers hide those). `ownReceiptId` ignores that receipt's own attachment, which can linger on
 * the cached transaction right after a detach.
 */
export const isReceiptLinkTransactionCandidate = (
  transaction: TransactionRecord | TransactionProps,
  ownReceiptId?: string,
): boolean =>
  transaction.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
  !transaction.attachments?.some(
    (attachment) => isReceiptAttachment(attachment) && (ownReceiptId === undefined || attachment.id !== ownReceiptId),
  );

/** Only a ready, service-owned, unlinked receipt can offer a direct attachment. */
export const canSuggestReceiptTransaction = (receipt: Receipt | undefined): receipt is Receipt =>
  Boolean(receipt && receipt.status === 'READY' && !receipt.transactionId && !isLocalReceipt(receipt));

// Only derive an alternate amount when subtotal + tax explains the total, with or without the tip.
// A present but inconsistent tip is not evidence for a broader guessed-tip search.
const getKnownTipAmountCents = ({ total, subtotal, tax, tip }: ReceiptMatchCriteria): number | undefined => {
  if (
    total === undefined ||
    total <= 0 ||
    subtotal === undefined ||
    !Number.isFinite(subtotal) ||
    subtotal <= 0 ||
    tax === undefined ||
    !Number.isFinite(tax) ||
    tax < 0 ||
    tip === undefined ||
    !Number.isFinite(tip) ||
    toCents(tip) <= AMOUNT_TOLERANCE_CENTS
  ) {
    return undefined;
  }
  const totalCents = toCents(total);
  const baseCents = toCents(subtotal) + toCents(tax);
  const tipCents = toCents(tip);
  if (Math.abs(totalCents - baseCents) <= AMOUNT_TOLERANCE_CENTS) return baseCents + tipCents;
  if (Math.abs(totalCents - baseCents - tipCents) <= AMOUNT_TOLERANCE_CENTS) return baseCents;
  return undefined;
};

/**
 * Exact amounts (within one cent) always win, using optional merchant/date narrowing as before.
 * Only when no exact match qualifies, try a reconciled known tip, then a bounded possible tip.
 * Both fallback tiers require a strong merchant, a valid date within three days, an expense, and
 * exactly one qualifying transaction. Ambiguity in the known-tip tier stops the search.
 */
export function findSuggestedTransactionMatch(
  transactions: ImmutableMap<string, TransactionRecord | TransactionProps> | undefined,
  criteria: ReceiptMatchCriteria,
): ReceiptTransactionMatch | undefined {
  const { total, merchant, transactionDate, accountIds, receiptId, today, tip } = criteria;
  if (!transactions || !canSearchForMatch(total)) {
    return undefined;
  }

  const totalCents = toCents(Math.abs(total));
  const accountSet = accountIds && accountIds.length > 0 ? new Set(accountIds) : undefined;
  const merchantQuery = merchant ? normalizeMerchantName(merchant) : '';
  const target = transactionDate ? DateTime.fromISO(transactionDate) : undefined;
  const hasTarget = !!target?.isValid;
  const windowStart = hasTarget ? target.minus({ days: DATE_WINDOW_DAYS }).toISODate() : undefined;
  const windowEnd = hasTarget ? target.plus({ days: DATE_WINDOW_DAYS }).toISODate() : undefined;
  const knownTipAmountCents = getKnownTipAmountCents(criteria);
  const canTryPossibleTip = total > 0 && (tip === undefined || tip === 0);
  const maxPossibleTipCents = Math.floor((totalCents * 30) / 100);

  const candidates: ReceiptMatchCandidate[] = [];

  transactions.forEach((txn) => {
    if (!txn?.id || !isReceiptLinkTransactionCandidate(txn, receiptId)) {
      return;
    }
    const { id, amount, postedOn, payee, accountId } = txn;
    if (accountSet && (!accountId || !accountSet.has(accountId))) {
      return;
    }
    // Never suggest a future-dated row (the register list hides them too).
    if (today && postedOn > today) {
      return;
    }
    if (hasTarget && windowStart && windowEnd && (postedOn < windowStart || postedOn > windowEnd)) {
      return;
    }

    const dayGap = hasTarget ? Math.abs(DateTime.fromISO(postedOn).diff(target, 'days').days) : 0;
    if (amount === undefined || !Number.isFinite(amount)) return;
    // Exact amounts continue to support expenses, refunds and net split amounts.
    const amountCents = toCents(Math.abs(amount));
    const differenceCents = amountCents - totalCents;
    const normalizedPayee = normalizeMerchantName(payee ?? '');
    let amountMatch: ReceiptTransactionMatch['amountMatch'];
    if (Math.abs(differenceCents) <= AMOUNT_TOLERANCE_CENTS) {
      amountMatch = 'exact';
    } else {
      // Amount relaxation must never operate on merchant/date alone, refunds or short generic names.
      if (
        !hasTarget ||
        !Number.isFinite(dayGap) ||
        dayGap > TIP_DATE_WINDOW_DAYS ||
        merchantQuery.length < 4 ||
        normalizedPayee.length < 4 ||
        amount >= 0
      )
        return;
      if (
        knownTipAmountCents !== undefined &&
        Math.abs(amountCents - knownTipAmountCents) <= AMOUNT_TOLERANCE_CENTS &&
        (amountCents > totalCents || txn.state === 'PENDING')
      ) {
        amountMatch = 'known-tip';
      } else if (
        canTryPossibleTip &&
        differenceCents > AMOUNT_TOLERANCE_CENTS &&
        differenceCents <= maxPossibleTipCents
      ) {
        amountMatch = 'possible-tip';
      } else {
        return;
      }
    }
    candidates.push({ id, payee: normalizedPayee, postedOn, dayGap, amountCents, amountMatch });
  });

  // Fuse searches in one direction, so collect whole-name matches before searching the remaining rows.
  // Only candidates satisfying amount, date, account and receipt eligibility reach either name check.
  const wholeWordCandidates = merchantQuery
    ? candidates.filter((item) => hasWholeWordNameMatch(merchantQuery, item.payee))
    : candidates;
  const wholeWordIds = new Set(wholeWordCandidates.map((item) => item.id));
  const matches = [
    ...wholeWordCandidates.map((item) => ({ item, score: 0 })),
    ...(merchantQuery
      ? new Fuse(
          candidates.filter((item) => !wholeWordIds.has(item.id)),
          MERCHANT_MATCH_OPTIONS,
        ).search(merchantQuery)
      : []),
  ];

  let best: (ReceiptMatchCandidate & { merchantScore: number }) | undefined;
  const knownTipMatches: ReceiptMatchCandidate[] = [];
  const possibleTipMatches: ReceiptMatchCandidate[] = [];
  for (const { item, score } of matches) {
    // Whole-word matches already score zero; preserve existing payee substrings at the same rank.
    const merchantScore = !merchantQuery || item.payee.includes(merchantQuery) ? 0 : (score ?? 1);
    if (item.amountMatch !== 'exact') {
      if (merchantScore <= TIP_MERCHANT_THRESHOLD) {
        (item.amountMatch === 'known-tip' ? knownTipMatches : possibleTipMatches).push(item);
      }
      continue;
    }
    if (
      !best ||
      merchantScore < best.merchantScore ||
      (merchantScore === best.merchantScore &&
        (item.dayGap < best.dayGap || (item.dayGap === best.dayGap && item.postedOn > best.postedOn)))
    ) {
      best = { ...item, merchantScore };
    }
  }

  const fallback = knownTipMatches.length > 0 ? knownTipMatches : possibleTipMatches;
  const selected = best ?? (fallback.length === 1 ? fallback[0] : undefined);
  return selected
    ? {
        transactionId: selected.id,
        amountMatch: selected.amountMatch,
        receiptAmount: totalCents / 100,
        transactionAmount: selected.amountCents / 100,
      }
    : undefined;
}

/** Backwards-compatible ID-only view of the shared match result. */
/**
 * The suggestion for a receipt as it stands now. The detail screens re-run this against current store
 * state when Attach is pressed so a stale suggestion is never linked.
 */
export function findReceiptSuggestedTransactionMatch(
  receipt: Receipt | undefined,
  transactions: ImmutableMap<string, TransactionRecord | TransactionProps> | undefined,
  { accountIds, today }: Pick<ReceiptMatchCriteria, 'accountIds' | 'today'>,
): ReceiptTransactionMatch | undefined {
  if (!canSuggestReceiptTransaction(receipt)) {
    return undefined;
  }
  const { total, subtotal, tax, tip, merchant, transactionDate } = receipt.extraction ?? {};
  return findSuggestedTransactionMatch(transactions, {
    total,
    subtotal,
    tax,
    tip,
    merchant,
    transactionDate,
    accountIds,
    receiptId: receipt.id,
    today,
  });
}

export function findSuggestedTransactionMatchId(
  transactions: ImmutableMap<string, TransactionRecord | TransactionProps> | undefined,
  criteria: ReceiptMatchCriteria,
): string | undefined {
  return findSuggestedTransactionMatch(transactions, criteria)?.transactionId;
}
